import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { integrations } from './marketplaceData';

const InstallationPage = () => {
  const { integrationId, type } = useParams<{ integrationId: string; type: string }>();
  const navigate = useNavigate();

  const integration = integrations.find((i) => i.id === integrationId);

  if (!integration) {
    return (
      <div className="p-6 text-white">
        <p>Integration not found</p>
        <button onClick={() => navigate('/marketplace')} className="text-[#D12929] hover:underline mt-4">
          ← Back to Catalog
        </button>
      </div>
    );
  }

  const platformName = type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Unknown';

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
        <button onClick={() => navigate('/marketplace')} className="hover:text-white">
          Catalog
        </button>
        <span>›</span>
        <button onClick={() => navigate(`/marketplace/integration/${integrationId}`)} className="hover:text-white">
          {integration.name}
        </button>
        <span>›</span>
        <span className="text-white">Automox Automated Endpoint Management for SentinelOne</span>
      </div>

      {/* Header */}
      <div className="bg-[#1f1f1f] border border-[#474646] rounded-lg p-6 mb-6">
        <div className="flex items-start gap-4">
          {/* Logo */}
          <div className="w-16 h-16 bg-[#2e2e2f] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
            {integration.logoUrl ? (
              <img
                src={integration.logoUrl}
                alt={integration.name}
                className="w-14 h-14 object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).nextElementSibling!.classList.remove('hidden');
                }}
              />
            ) : null}
            <span className={`text-white font-bold text-xl ${integration.logoUrl ? 'hidden' : ''}`}>{integration.icon}</span>
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-white text-2xl font-bold">{integration.name}</h1>
              <span className="px-3 py-1 bg-[#D12929] text-white text-sm rounded">{integration.category}</span>
            </div>
            <p className="text-gray-400 mb-4">{integration.vendor}</p>
            <p className="text-gray-300">{integration.description}</p>
          </div>

          {/* Learn More Button */}
          <button className="px-6 py-2 bg-[#D12929] hover:bg-[#b71c1c] text-white font-semibold rounded-lg transition-colors">
            Learn More ↗
          </button>
        </div>
      </div>

      {/* Installation Info */}
      <div className="bg-[#1f1f1f] border border-[#474646] rounded-lg p-6">
        <div className="bg-[#2e2e2f] border border-[#474646] rounded p-4 mb-6">
          <p className="text-gray-300 text-sm">
            <strong>Note:</strong> This integration is partner-built and hosted by the vendor. For additional information see below, and
            contact the vendor.
          </p>
        </div>

        <p className="text-gray-300 mb-6">
          Automox Worklets can be accessed via the Automox console, within the Automox Community Worklets (these links are included below),
          or GitHub. Simply find and execute the specific SentinelOne worklet by your OS.
        </p>

        {/* Platform Specific Instructions */}
        <div className="bg-[#2e2e2f] border border-[#474646] rounded-lg p-6 mb-6">
          <h2 className="text-white text-lg font-semibold mb-4">Install SentinelOne Agent - {platformName}</h2>

          <div className="space-y-4">
            <div>
              <h3 className="text-white font-semibold mb-2">Installation Steps:</h3>
              <ol className="list-decimal list-inside text-gray-300 space-y-2">
                <li>Log in to your Automox console</li>
                <li>Navigate to the Worklets section</li>
                <li>Search for "SentinelOne {platformName}" in the Community Worklets</li>
                <li>Select the appropriate worklet for your {platformName} systems</li>
                <li>Configure the worklet settings according to your organization's requirements</li>
                <li>Assign the worklet to the target device groups</li>
                <li>Set the execution schedule</li>
                <li>Save and activate the worklet</li>
              </ol>
            </div>

            <div className="bg-[#1f1f1f] border border-[#474646] rounded p-4">
              <h3 className="text-white font-semibold mb-2">Requirements:</h3>
              <ul className="list-disc list-inside text-gray-300 space-y-1">
                <li>Active Automox subscription</li>
                <li>SentinelOne management console access</li>
                <li>Appropriate permissions to deploy worklets</li>
                <li>{platformName} devices with Automox agent installed</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="space-y-4 text-gray-300">
          <p>Once deployed, select the schedule and scope of the Worklets to be performed per your organization's specific requirements.</p>

          <p>
            The Automox platform will then perform regularly scheduled scans to confirm the SentinelOne agent is installed. For those
            devices that do not have a SentinelOne agent detected, Automox will automatically install the agent per your policy targets and
            timing.
          </p>

          <div className="bg-[#2e2e2f] border border-[#474646] rounded p-4">
            <h3 className="text-white font-semibold mb-2">Need Help?</h3>
            <p>
              For further information and support, contact{' '}
              <a href="mailto:partner@automox.com" className="text-blue-400 hover:underline">
                partner@automox.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallationPage;
